import Button from "components/Button";
import Content from "components/Content";
import Form from "components/Form";
import Label from "components/Label";
import React, { FC } from "react";
import Media from "components/Media";
import toast from "react-hot-toast";
import axios, { AxiosError } from "axios";
import useAsync from "hooks/use-async";
import {
  formatErrors,
  formatHSErrors,
  HSPayload,
  required,
  validEmail
} from "utils/form";
import DesktopBackground from "../../../../public/desktop-background.svg";
import MobileBackground from "../../../../public/mobile-background.svg";

import styles from "./styles.module.scss";
import { vendorOptions } from "./constants";

type Props = {
  fillColor?: string;
};

type FormValues = {
  firstname: string;
  phone: string;
  email: string;
  service: string;
};

const StartHummingVendor: FC<Props> = ({ fillColor = "white" }) => {
  const [submit, submitting] = useAsync(async (values: FormValues) => {
    try {
      await axios.post(
        `https://api.hsforms.com/submissions/v3/integration/submit/21008183/738c20e8-582b-4305-a29a-7c06160f6e45`,
        {
          fields: Object.keys(values).map((key) => ({
            name: key,
            value: values[key as keyof FormValues]
          }))
        }
      );
      toast.success("We got your information!");
    } catch (e) {
      const payload = (e as AxiosError).response?.data;
      toast.error(
        payload ? formatHSErrors(payload as HSPayload) : "Something went wrong"
      );
    }
  });

  return (
    <Content
      disableMotion
      containerClassName={styles.wrapper}
      contentClassName={styles.container}
    >
      <>
        <Media at={["desktop", "tablet"]}>
          <DesktopBackground
            className={`${styles.background} ${styles[`${fillColor}Fill`]}`}
          />
        </Media>
        <Media at={["mobile"]}>
          <MobileBackground className={styles.background} />
          <div
            className={`${styles.mobileFill} ${styles[`${fillColor}Fill`]}`}
          />
        </Media>
        <div id="form" className={styles.content}>
          <Label variant="h2-centered" color="primary2">
            Join our service provider network
          </Label>
          <Label
            className={styles.subtitle}
            variant="s2-centered"
            color="primary2"
          >
            ... and let us work for you.
          </Label>
          <Media className={styles.media} at={["desktop", "tablet"]}>
            <Form
              onError={(errors) =>
                toast.error(formatErrors(errors), { position: "top-right" })
              }
              className={styles.form}
              onSubmit={submit}
              fields={[
                [
                  {
                    id: "firstname",
                    type: "text",
                    placeholder: "Full name",
                    validate: [required("Please enter your first name")]
                  },
                  {
                    id: "email",
                    placeholder: "Email address",
                    type: "text",
                    validate: [
                      required("Please enter your email"),
                      validEmail("Invalid email")
                    ]
                  },
                  {
                    id: "phone",
                    placeholder: "Phone number",
                    type: "text",
                    validate: [required("Please enter your phone number")]
                  }
                ],
                {
                  id: "0-2/service_categories__drop_down_",
                  type: "select",
                  options: vendorOptions,
                  title: "Which service do you provide?",
                  validate: [required("Please enter a service category")]
                }
              ]}
            >
              {({ invalid }) => (
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={invalid}
                  className={styles.submit}
                  variant="accent"
                >
                  Join now
                </Button>
              )}
            </Form>
          </Media>
          <Media className={styles.media} at={["mobile"]}>
            <Form
              onError={(errors) =>
                toast.error(formatErrors(errors), { position: "top-right" })
              }
              className={styles.form}
              onSubmit={submit}
              fields={[
                {
                  id: "firstname",
                  type: "text",
                  placeholder: "Full name",
                  validate: [required("Please enter your first name")]
                },
                {
                  id: "email",
                  placeholder: "Email address",
                  type: "text",
                  validate: [
                    required("Please enter your email"),
                    validEmail("Invalid email")
                  ]
                },
                {
                  id: "phone",
                  placeholder: "Phone number",
                  type: "text",
                  validate: [required("Please enter your phone number")]
                },
                {
                  id: "0-2/service_categories__drop_down_",
                  type: "select",
                  options: vendorOptions,
                  title: "Which service do you provide?",
                  validate: [required("Please enter a service category")]
                }
              ]}
            >
              {({ invalid }) => (
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={invalid}
                  className={styles.submit}
                  variant="accent"
                >
                  Join now
                </Button>
              )}
            </Form>
          </Media>
        </div>
      </>
    </Content>
  );
};

StartHummingVendor.defaultProps = {
  fillColor: "primary"
};

export default StartHummingVendor;
